// Table Hooks
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useActionsFirstColumnOrder from '../../hooks/useActionsFirstColumnOrder';
import useManageAffordableWaitlistQuery from './hooks.useManageAffordableWaitlistQuery';
import useRows from './hooks.useRows';
import {
  useTableFilterSortSearchManager,
  useTableFilterSortData,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useHeaders from './hooks.useHeaders';

// Other Hooks
import useModalProps from '../../hooks/useModalProps';
import useAffordableWaitlistCSVorPDFExport from './hooks.useAffordableWaitlistCSVorPDFExport';
import useParseResults from './hooks.useParseResults';

// Constants
import { PROPERTY_PATH_MAP } from './constants';
import useManageAffordableWaitlistPropertyPreferences from './hooks.useManageAffordableWaitlistPropertyPreferences';
import { useEffect } from 'react';

const useManageAffordableWaitlist = ({
  organizationId,
  propertyId,
  filter,
  isAffordableWaitlistStatusClosed,
}) => {
  const name = useUniqueTableName('manage-affordable-waitlist-table');

  const {
    data,
    isLoading,
    refetch: refetchAffordableWaitlists,
  } = useManageAffordableWaitlistQuery({
    organizationId,
    propertyId,
    filter,
  });
  // Parse results
  const { parsedResults } = useParseResults({ results: data ?? [] });
  const headers = useHeaders({
    results: parsedResults,
  });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'position',
    initialOrder: 'ASC',
    initialFilterState: {
      status: ['waitlist'],
      householdStatus: ['applicant'],
    },
  });

  // Synchronize filter status checkboxes with the "Include All" option
  useEffect(() => {
    // When filter.includeAll changes, update the status filter
    if (filter?.includeAll) {
      handleFilterChange('status', ['canceled', 'waitlist', 'denied']);
    } else {
      handleFilterChange('status', ['waitlist']);
    }
  }, [filter?.includeAll, handleFilterChange]);

  const sortedAndFilteredResults = useTableFilterSortData({
    results: parsedResults,
    order,
    orderBy,
    filterState: {
      ...filterState,
      status: filterState.status,
    },
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  // Custom handler to sync checkbox state with "select all" option
  const handleFilterChangeWithSelectAll = (name, value) => {
    const isClearAllStatus =
      name === 'status' && Array.isArray(value) && value.length === 0;
    if (isClearAllStatus) {
      handleFilterChange('status', []);
      return;
    }
    handleFilterChange(name, value);
    if (name === 'status' && filter?.setIncludeAll) {
      const allStatuses = ['canceled', 'waitlist', 'denied'];
      const allSelected = allStatuses.every((s) => value.includes(s));
      filter.setIncludeAll(allSelected);
    }
  };

  const modalProps = useModalProps();

  const rows = useRows({
    affordableWaitlistItems: sortedAndFilteredResults,
    modalProps,
    isAffordableWaitlistStatusClosed,
  });

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  const { onCSVButtonClick, onPDFButtonClick } =
    useAffordableWaitlistCSVorPDFExport({
      filteredHeaders,
      rows,
      hasAnyFilters: Boolean(Object.keys(filterState)?.length),
    });

  // Configure Preferences
  const {
    data: affordableWaitlistPropertyPreferencesData,
    refetch: refetchAffordableWaitlistPropertyPreferences,
  } = useManageAffordableWaitlistPropertyPreferences({
    organizationId,
    propertyId,
  });

  const defaultValuesConfigurePreferences =
    affordableWaitlistPropertyPreferencesData?.preferences?.reduce(
      (acc, key) => {
        acc[key] = true;
        return acc;
      },
      {},
    );

  return {
    modalProps,
    // Handle Sort/Filter/Search
    handleSortChange,
    handleFilterChange: handleFilterChangeWithSelectAll,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    // Columns
    handleColumnChange,
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    // Indicators
    isLoading,
    count: rows.length ?? 0,
    totalCount: sortedAndFilteredResults.length ?? 0,
    name,
    order,
    orderBy,
    // Data
    filteredHeaders,
    refetchAffordableWaitlists,
    rows,
    onCSVButtonClick,
    onPDFButtonClick,
    affordableWaitlistPropertyPreferencesData,
    refetchAffordableWaitlistPropertyPreferences,
    defaultValuesConfigurePreferences: {
      id: affordableWaitlistPropertyPreferencesData?.id,
      options: defaultValuesConfigurePreferences,
    },
  };
};

export default useManageAffordableWaitlist;
